import { ReactNode } from 'react';
import { ReportEvaluation, ReportFinding } from '@/reports/types';
import { isOpen, kindOf } from '@/reports/verdict';
import { SOURCE_LABELS, ValueSource } from '@/reports/provenance';
import { verdictClass } from './ReportSheet';

export type PillState = 'pass' | 'warn' | 'fail' | 'none';

export function StatusPill({ state, children }: { state: PillState; children: ReactNode }) {
  return <span className={`status-pill ${state}`}>{children}</span>;
}

export interface TileData {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  state?: PillState;
}

export function StatTiles({ tiles }: { tiles: TileData[] }) {
  return (
    <div className="tiles">
      {tiles.map((t) => (
        <div key={t.label} className={`tile ${t.state && t.state !== 'none' ? t.state : ''}`}>
          <span className="tile-label">{t.label}</span>
          <span className="tile-value">{t.value}</span>
          {t.sub && <span className="tile-sub">{t.sub}</span>}
        </div>
      ))}
    </div>
  );
}

export interface KeyValueData {
  label: string;
  value: ReactNode;
}

/** Two-column label/value grid: the summary block. */
export function SummaryGrid({ items, title }: { items: KeyValueData[]; title?: string }) {
  return (
    <>
      {title && <p className="block-title">{title}</p>}
      <div className="summary-grid">
        {items.map((kv, i) => (
          <dl key={`${kv.label}-${i}`} className="kv"><dt>{kv.label}</dt><dd>{kv.value}</dd></dl>
        ))}
      </div>
    </>
  );
}

const KIND_STATE = { pass: 'pass', defect: 'fail', invalid: 'fail', gap: 'warn', note: 'info' } as const;
const KIND_BADGE = { pass: 'PASS', defect: 'FAIL', invalid: 'INVALID', gap: 'OPEN', note: 'NOTE' } as const;

/** One check the report makes, in the left-ruled note form. */
export function Check({ finding }: { finding: ReportFinding }) {
  const kind = kindOf(finding);
  const state = KIND_STATE[kind];
  const badge = finding.kind ? KIND_BADGE[kind] : finding.passed ? 'PASS' : finding.severity === 'fail' ? 'FAIL' : finding.severity === 'warn' ? 'WARN' : 'INFO';
  return (
    <div className={`check ${state}`} role="note" aria-label={finding.id}>
      <span className="check-badge">{badge}</span>
      <span className="check-msg">{finding.message}</span>
    </div>
  );
}

export function Callout({ variant = 'info', title, children }: { variant?: 'info' | 'warning' | 'danger'; title?: ReactNode; children: ReactNode }) {
  return (
    <div className={`callout ${variant === 'info' ? '' : variant}`} role="note">
      {title && <div className="callout-title">{title}</div>}
      {children}
    </div>
  );
}

/** Findings listed under the header, as the report summarises them. */
export function FindingsSummary({ findings, title = 'Findings' }: { findings: ReportFinding[]; title?: string }) {
  const open = findings.filter((f) => (f.kind ? isOpen(f) : !f.passed));
  if (open.length === 0) return null;
  return (
    <div className="findings">
      <div className="findings-head">{title}</div>
      <ul>
        {open.map((f) => (
          <li key={f.id}><span className="sev-tag">{f.kind ? KIND_BADGE[kindOf(f)] : f.severity.toUpperCase()}</span>{f.message}</li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The result, first: the verdict with a few figures that frame it, and the
 * findings still open. What a reader should see before any detail, under the
 * header of every composed report.
 */
export function ReportSummary({ evaluation, tiles = [] }: { evaluation: ReportEvaluation; tiles?: TileData[] }) {
  const state = verdictClass(evaluation.verdict) === 'passed' ? 'pass' : verdictClass(evaluation.verdict) === 'failed' ? 'fail' : 'warn';
  return (
    <div className="report-summary">
      <StatTiles tiles={[{ label: 'Result', value: evaluation.verdictLabel, state }, ...tiles]} />
      <FindingsSummary findings={evaluation.findings} title="Open findings" />
    </div>
  );
}

/** Where a value came from, when it isn't the run: the template, the report, a guess. Nothing for a value the run recorded. */
export function SourceMark({ source, note }: { source: ValueSource | null | undefined; note?: string }) {
  if (!source || source === 'run') return null;
  return <span className={`source-mark source-${source}`} title={note ?? SOURCE_LABELS[source]}>{SOURCE_LABELS[source]}</span>;
}

